import React, { useState, useEffect } from 'react';
import { Palette, Copy, Check } from 'lucide-react';
import './ToolsCommon.css';

const ColorPicker = () => {
  const [color, setColor] = useState('#4f46e5');
  const [copiedFormat, setCopiedFormat] = useState('');

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const hexToHsl = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return null;

    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  const rgb = hexToRgb(color);
  const hsl = hexToHsl(color);

  const copyToClipboard = async (text, format) => {
    await navigator.clipboard.writeText(text);
    setCopiedFormat(format);
    setTimeout(() => setCopiedFormat(''), 2000);
  };

  const formats = [
    { name: 'HEX', value: color.toUpperCase() },
    { name: 'RGB', value: rgb ? `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` : '' },
    { name: 'RGBA', value: rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)` : '' },
    { name: 'HSL', value: hsl ? `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)` : '' },
  ];

  return (
    <div className="tool-container">
      <div className="tool-content-wrapper">
        <div className="tool-header">
          <Palette size={48} className="tool-icon" />
          <h1 className="tool-title">Color Picker & Converter</h1>
          <p className="tool-subtitle">Pick colors and convert between formats</p>
        </div>

        <div className="tool-panel">
          <div className="tool-section">
            <label className="tool-label">Pick a Color</label>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                style={{
                  width: '100px',
                  height: '100px',
                  border: '4px solid #e5e7eb',
                  borderRadius: '16px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="tool-input"
                placeholder="#000000"
                style={{ flex: 1 }}
              />
            </div>
          </div>

          <div className="color-preview" style={{ backgroundColor: color }}></div>

          <div className="tool-section">
            <label className="tool-label">Color Formats</label>
            <div className="color-info">
              {formats.map((format) => (
                <div key={format.name} className="color-value">
                  <div className="color-value-label">{format.name}</div>
                  <div className="color-value-text">{format.value}</div>
                  <button
                    onClick={() => copyToClipboard(format.value, format.name)}
                    className="tool-button secondary"
                    style={{ marginTop: '8px', padding: '8px 16px', fontSize: '12px' }}
                  >
                    {copiedFormat === format.name ? <Check size={14} /> : <Copy size={14} />}
                    {copiedFormat === format.name ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="tool-section">
            <label className="tool-label">Popular Color Palettes</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))', gap: '12px', marginTop: '16px' }}>
              {[
                '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
                '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
                '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#000000',
                '#ffffff', '#6b7280'
              ].map((presetColor) => (
                <div
                  key={presetColor}
                  onClick={() => setColor(presetColor)}
                  style={{
                    backgroundColor: presetColor,
                    height: '50px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: color === presetColor ? '3px solid #4f46e5' : '2px solid #e5e7eb',
                    transition: 'all 0.2s'
                  }}
                  title={presetColor}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorPicker;
